import axios from "axios";
import moment from "moment";
import {
  url_14,
  url_15_1,
  url_61,
  url_217,
  url_245,
  url_304,
  url_305,
  url_291,
  url_292,
  url_293,
  url_294,
  url_306,
  url_307,
  url_314,
  url_315,
} from "../../../custom_utilities/api_services";
import {
  collectLocalStorageResumeTests,
  dedupeResumeItems,
  mapLegacyUserTestToResumeItem,
} from "../utils/resumeTestUtils";
import {
  extractSchedulePayload,
  mapScheduleItems,
} from "../utils/dailyPathUtils";
import {
  mergeExploreCategories,
  normalizeStoreCatalog,
} from "../../DashboardCourses/utils/exploreStore";

const authHeaders = (authorization) =>
  authorization ? { Authorization: authorization } : {};

export const fetchStudyDashboardProgress = async (authorization) => {
  const response = await axios.get(url_293, { headers: authHeaders(authorization) });
  if (response?.data?.status === 200) return response.data.data;
  return null;
};

export const fetchStudyDashboardStreak = async (authorization) => {
  const response = await axios.get(url_291, { headers: authHeaders(authorization) });
  if (response?.data?.status === 200) return response.data.data;
  return null;
};

export const fetchStudyDashboardLeaderboard = async (authorization) => {
  const response = await axios.get(url_292, { headers: authHeaders(authorization) });
  if (response?.data?.status === 200) return response.data.data;
  return null;
};

export const fetchStudyDashboardResumeVideo = async (authorization) => {
  const response = await axios.get(url_315, { headers: authHeaders(authorization) });
  if (response?.data?.status === 200) return response.data.data;
  return null;
};

export const fetchStudyDashboardResumeTests = async (
  authorization,
  { courseId, batchId, classId } = {}
) => {
  const collected = [...collectLocalStorageResumeTests()];

  if (courseId && batchId && classId) {
    try {
      const response = await axios.post(
        url_61,
        {
          course_id: courseId,
          class_id: classId,
          batch_id: batchId,
          test_type: 3,
          page: 1,
        },
        { headers: authHeaders(authorization) }
      );

      const testlist = response?.data?.data?.testlist;
      if (response?.data?.status === 200 && Array.isArray(testlist)) {
        testlist
          .filter((test) => test?.is_resume && !test?.is_attempt)
          .forEach((test) => {
            const mapped = mapLegacyUserTestToResumeItem(test);
            if (mapped) collected.push(mapped);
          });
      }
    } catch {
      // ignore
    }
  }

  return dedupeResumeItems(collected);
};
export const fetchDashboardExploreStore = async (authorization) => {
  const headers = authHeaders(authorization);

  const [exploreResponse, storeResponse] = await Promise.all([
    axios.get(url_304, { headers }).catch(() => null),
    axios.get(`${url_217}?platform=website`, { headers }).catch(() => null),
  ]);

  const exploreRoot =
    exploreResponse?.data?.status === 200 ? exploreResponse.data.data : null;
  const explore =
    exploreRoot?.explore && typeof exploreRoot.explore === "object"
      ? exploreRoot.explore
      : exploreRoot?.topper_choice ||
          exploreRoot?.recommended ||
          exploreRoot?.handbook ||
          exploreRoot?.overall
        ? exploreRoot
        : {};

  const exploreOverall = normalizeStoreCatalog(
    explore?.overall ?? exploreRoot?.overall
  );
  const storeCatalog =
    storeResponse?.data?.status === 200
      ? normalizeStoreCatalog(storeResponse.data.data)
      : { category_list: [], packages: [] };

  const overall = {
    ...explore?.overall,
    category_list: mergeExploreCategories(
      exploreOverall.category_list,
      storeCatalog.category_list
    ),
    packages: exploreOverall.packages.length
      ? exploreOverall.packages
      : storeCatalog.packages,
  };

  if (
    !overall.category_list.length &&
    !overall.packages.length &&
    !explore?.topper_choice &&
    !explore?.recommended &&
    !explore?.handbook
  ) {
    return null;
  }

  return {
    ...explore,
    overall,
  };
};

export const fetchDashboardHandbookList = async (authorization, productId = "") => {
  const url = productId ? `${url_245}?product_id=${productId}` : url_245;
  const response = await axios.get(url, { headers: authHeaders(authorization) });
  const data = response?.data?.data;
  return Array.isArray(data) ? data : [];
};

export const fetchDashboardStoreBanner = async (authorization) => {
  const response = await axios.get(`${url_217}?platform=website`, {
    headers: authHeaders(authorization),
  });
  if (response?.data?.status === 200) return response.data.data?.banner ?? null;
  return null;
};

export const fetchDashboardHomeSliderBanners = async (authorization) => {
  const response = await axios.get(url_305, { headers: authHeaders(authorization) });
  if (response?.data?.status === 200 && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  return [];
};

export const fetchStudyDashboardDailyGoal = async (authorization) => {
  const response = await axios.get(url_294, { headers: authHeaders(authorization) });
  if (response?.data?.status === 200) return response.data.data;
  return null;
};

export const fetchStudyDashboardTopWeakTopics = async (authorization) => {
  const response = await axios.get(url_306, { headers: authHeaders(authorization) });
  if (response?.data?.status === 200) return response.data.data;
  return null;
};

const ANALYTICS_REPORT_URL =
  "https://learning.motion.ac.in/motioneducation/api/v1/getAnalyticsReport";

const parseAccuracyValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const num = Number(String(value).replace("%", "").trim());
  return Number.isFinite(num) ? num : null;
};

/**
 * For each Recent Tests card: call getAnalyticsReport with paper_id / paper_type /
 * planner_test_id from testWiseAnalysis, then use that accuracy on the ring.
 */
const fetchRecentTestAccuracyFromAnalytics = async (authorization, test) => {
  const paperType = test?.paper_type ?? test?.paperType;
  const paperId = test?.paper_id ?? test?.paperId;
  if (paperType == null || paperId == null) return test;

  try {
    let url = `${ANALYTICS_REPORT_URL}?paper_type=${paperType}&paper_id=${paperId}`;
    const plannerId = Number(test?.planner_test_id ?? test?.plannerTestId ?? 0);
    if (Number.isFinite(plannerId) && plannerId > 0) {
      url += `&planner_test_id=${plannerId}`;
    }

    const res = await axios.get(url, { headers: authHeaders(authorization) });
    const report = res?.data?.data;
    const accuracy = parseAccuracyValue(report?.accuracy);
    if (accuracy == null) return test;

    return { ...test, accuracy };
  } catch {
    return test;
  }
};

export const fetchStudyDashboardTestWiseAnalysis = async (authorization) => {
  const response = await axios.get(url_307, { headers: authHeaders(authorization) });
  if (response?.data?.status !== 200) return null;

  const data = response.data.data;
  const recent = Array.isArray(data?.recent_tests) ? data.recent_tests : [];
  // Only enrich cards shown on dashboard (top 2).
  const top = await Promise.all(
    recent
      .slice(0, 2)
      .map((test) => fetchRecentTestAccuracyFromAnalytics(authorization, test))
  );

  return {
    ...data,
    recent_tests: [...top, ...recent.slice(2)],
  };
};

export const fetchStudyDashboardSubjectPerformance = async (authorization) => {
  const response = await axios.get(url_314, { headers: authHeaders(authorization) });
  if (response?.data?.status === 200) return response.data.data;
  return null;
};

export const fetchStudyDashboardScheduleWeek = async (authorization, courseId, batchId) => {
  const response = await axios.get(
    `${url_14}?course_id=${courseId}&batch_id=${batchId}`,
    { headers: authHeaders(authorization) }
  );
  const week = response?.data?.data?.ScheduleWeek;
  if (response?.data?.status !== 200 || !Array.isArray(week)) return [];

  return week.map((entry) => {
    const date = moment(entry?.date, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
    const normalizedDate = date.isValid() ? date.format("YYYY-MM-DD") : String(entry?.date || "");
    return {
      key: normalizedDate,
      label: date.isValid() ? date.format("ddd") : String(entry?.day || "").slice(0, 3),
      date: date.isValid() ? date.format("D") : String(entry?.date || ""),
      isToday: date.isValid() ? date.isSame(moment(), "day") : false,
      rawDate: normalizedDate,
      dayName: entry?.day,
    };
  });
};

export const fetchStudyDashboardScheduleContent = async (
  authorization,
  courseId,
  batchId,
  dayName,
  rawDate
) => {
  const response = await axios.post(
    url_15_1,
    {
      course_id: courseId,
      batch_id: batchId,
      day: dayName,
      date: rawDate,
    },
    { headers: authHeaders(authorization) }
  );

  if (response?.data?.status !== 200) {
    return { items: [], homework: [] };
  }

  const schedulePayload = extractSchedulePayload(response.data.data);
  return {
    items: mapScheduleItems(schedulePayload),
    homework: schedulePayload.homework,
  };
};
